import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import CommonModal from "./CommonModal";
import { callAPI } from "../../utils/apiUtils";
import { apiUrls } from "../../utils/apiUrls";
import { buildScheduleFields } from "../../utils/offerSchedule";
import { promoToForm } from "../../utils/promoEdit";
import { USING_PROMO_SCHEDULE_MOCK, mockPromoApi } from "../../utils/promoScheduleService";
import PromotionScheduleControls, {
  emptyScheduleForm,
  toScheduleArgs,
  useTargetOptions,
  validateScheduleControls,
} from "../promotions/PromotionScheduleControls";

const ORDER_TYPE_OPTIONS = [
  { value: "delivery", label: "Delivery" },
  { value: "takeaway", label: "Takeaway" },
  { value: "table_ordering", label: "Table Ordering" },
  { value: "finedining", label: "Fine Dining" },
  { value: "roomservice", label: "Room Service" },
];

// Payment mode ids match the real 99_paymentmode table (also what the admin's own "Add Offer"
// form lists): 1 = Cash On Delivery, 2 = EFTPOS, 3 = Online Payment.
const PAYMENT_MODE_OPTIONS = [
  { value: "1", label: "Cash On Delivery" },
  { value: "2", label: "EFTPOS" },
  { value: "3", label: "Online Payment" },
];

const emptyForm = () => ({
  promo_code: "",
  offer_type: "per",
  offer_amount: "",
  max_discount_amount: "",
  min_amount: "",
  valid_from_date: moment().format("YYYY-MM-DD"),
  valid_from_time: moment().format("HH:mm"),
  valid_to_date: moment().add(30, "days").format("YYYY-MM-DD"),
  valid_to_time: moment().format("HH:mm"),
  offer_types: ["delivery", "takeaway", "table_ordering"],
  offer_valid_for: ["1", "2", "3"],
  one_time_use_only: false,
  offer_available_web: false,
  offer_dish_mode: "all",
  offer_dish_ids: [],
  is_coupon: false,
  details: "",
  // Only used when the server reports features.offer_schedule (see offerScheduleEnabled below).
  ...emptyScheduleForm(),
});

/**
 * Same underlying `99_offertbl` feature the admin's "Manage Offers" screen writes to (mirrors
 * OffersRepository::createOrUpdate on the backend), scoped down for a quick in-POS add. A voucher
 * (is_coupon=1) is only ever redeemed by its code being entered at checkout, never auto-listed
 * there based on cart value the way a plain promo is - but it still shows up in this management
 * screen (see Promotions.js's Type column), it just behaves differently at checkout, not here.
 * The promo image is optional since this listing never displays it.
 *
 * `offerScheduleEnabled` is the listing's `features.offer_schedule`. When it is false (the default,
 * and what an older backend implies) nothing below changes: same controls, same request.
 *
 * `promo` (a row of the listing) switches the modal to EDIT mode: the form is pre-filled from it,
 * the request goes to update-offer with the promotion's id, and the promo/voucher type is locked
 * (the server fixes it at creation). Without `promo` it is the Add modal, unchanged.
 */
const AddPromotionModal = ({
  isOpen,
  onClose,
  onCreated,
  promo = null,
  authPin = null,
  onPinRejected = null,
  offerScheduleEnabled = false,
}) => {
  const isEdit = !!promo;
  const [form, setForm] = useState(emptyForm());
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dishes, setDishes] = useState([]);
  const [dishesLoaded, setDishesLoaded] = useState(false);
  const [dishSearch, setDishSearch] = useState("");
  const targetOptions = useTargetOptions();
  // Schedule + Applies-to replace the old All/Specific Dishes control for normal promotions when
  // the feature is on; vouchers never get them.
  const scheduleActive = offerScheduleEnabled && !form.is_coupon;

  useEffect(() => {
    if (isOpen && !dishesLoaded) {
      (USING_PROMO_SCHEDULE_MOCK ? mockPromoApi("dish-options") : callAPI(apiUrls.PROMO_DISH_OPTIONS, {}, "GET"))
        .then((res) => {
          setDishes(res.data?.data || []);
          setDishesLoaded(true);
        })
        .catch((err) => {
          console.error("Failed to load dish options:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Edit mode: fill the form from the promotion each time the modal opens for one.
  useEffect(() => {
    if (isOpen && promo) {
      setForm(promoToForm(promo, offerScheduleEnabled, emptyForm()));
      setImageFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, promo]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const toggleInArray = (field, value) => {
    setForm((prev) => {
      const current = prev[field];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
  };

  const resetAndClose = () => {
    setForm(emptyForm());
    setImageFile(null);
    setDishSearch("");
    targetOptions.reset();
    onClose();
  };

  const validate = () => {
    if (!form.promo_code.trim()) return "Promo code is required.";
    if (form.offer_amount === "" || Number(form.offer_amount) <= 0) return "Enter a discount amount.";
    if (form.max_discount_amount === "" || Number(form.max_discount_amount) <= 0) return "Enter a max discount cap.";
    if (form.min_amount === "") return "Enter a minimum order amount.";
    if (!form.valid_from_date || !form.valid_from_time) return "Set when this promotion starts.";
    if (!form.valid_to_date || !form.valid_to_time) return "Set when this promotion ends.";
    if (form.offer_types.length === 0) return "Select at least one order type this applies to.";
    if (form.offer_valid_for.length === 0) return "Select at least one payment mode this applies to.";
    if (scheduleActive) {
      return validateScheduleControls(form);
    }
    if (form.offer_dish_mode === "specific" && form.offer_dish_ids.length === 0) {
      return "Select at least one product, or switch to All Dishes.";
    }
    return null;
  };

  const filteredDishes = dishSearch.trim()
    ? dishes.filter((d) => d.name.toLowerCase().includes(dishSearch.trim().toLowerCase()))
    : dishes;

  const handleSave = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    // No PIN (the previous one was refused and the re-prompt was dismissed): ask again instead of
    // sending a request the server is certain to refuse.
    if (!authPin) {
      onPinRejected && onPinRejected("");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      if (isEdit) formData.append("id", promo.id);
      formData.append("promo_code", form.promo_code.trim());
      // The server is the authority on this PIN (a manager's staff PIN with
      // marketing.create_promotion, or the restaurant's super admin PIN) and checks it on every
      // create. Sent in the body, never the URL.
      formData.append("pin", authPin);
      formData.append("is_coupon", form.is_coupon ? "1" : "0");
      formData.append("offer_type", form.offer_type);
      formData.append("offer_amount", form.offer_amount);
      formData.append("max_discount_amount", form.max_discount_amount);
      formData.append("min_amount", form.min_amount);
      formData.append("valid_from_date", form.valid_from_date);
      formData.append("valid_from_time", form.valid_from_time);
      formData.append("valid_to_date", form.valid_to_date);
      formData.append("valid_to_time", form.valid_to_time);
      form.offer_types.forEach((t) => formData.append("offer_types[]", t));
      form.offer_valid_for.forEach((m) => formData.append("offer_valid_for[]", m));
      formData.append("one_time_use_only", form.one_time_use_only ? "1" : "0");
      formData.append("offer_available_web", form.offer_available_web ? "1" : "0");
      if (!scheduleActive && form.offer_dish_mode === "specific") {
        form.offer_dish_ids.forEach((id) => formData.append("offer_dish[]", id));
      } else {
        formData.append("offer_dish[]", "all");
      }
      // Additive fields, sent only when the server turned the feature on for this restaurant.
      buildScheduleFields({
        enabled: offerScheduleEnabled,
        isCoupon: form.is_coupon,
        ...toScheduleArgs(form),
      }).forEach(([key, value]) => formData.append(key, value));
      formData.append("details", form.details);
      if (imageFile) {
        formData.append("offer_image", imageFile);
      }

      const res = USING_PROMO_SCHEDULE_MOCK
        ? await mockPromoApi("create", formData)
        : await callAPI(
            isEdit ? apiUrls.PROMO_UPDATE : apiUrls.PROMO_CREATE,
            {},
            "POST",
            formData,
            {},
            { "Content-Type": "multipart/form-data" }
          );

      if (res.data?.status === 1) {
        toast.success(isEdit ? "Promotion updated successfully" : "Promotion created successfully");
        onCreated && onCreated();
        resetAndClose();
      } else {
        toast.error(res.data?.message || (isEdit ? "Failed to update promotion" : "Failed to create promotion"));
      }
    } catch (err) {
      console.error(isEdit ? "Failed to update promotion:" : "Failed to create promotion:", err);
      // The API's 4xx helpers put the human-readable text in `error` (403 not authorised, 400
      // validation); the 422 model-validation shape uses `message`.
      const data = err.response?.data;
      toast.error(data?.error || data?.message || (isEdit ? "Failed to update promotion" : "Failed to create promotion"));
      if (err.response?.status === 403) {
        // Refused (wrong/unauthorised PIN, or the permission was revoked since it was entered):
        // keep what was typed into the form and ask for the PIN again, showing the server's reason.
        onPinRejected && onPinRejected(data?.error || "");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonModal isOpen={isOpen} onClose={resetAndClose} elId="addPromotionModal">
      <div className="modal-header">
        <h5 className="modal-title">{isEdit ? "Edit Promotion" : "Add Promotion"}</h5>
        <button className="btn-close" onClick={resetAndClose}></button>
      </div>
      <div className="modal-body">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Type of Promo</label>
            <div className="d-flex gap-3" style={{ height: 38, alignItems: "center" }}>
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  className="me-1"
                  checked={!form.is_coupon}
                  disabled={isEdit}
                  onChange={() => update("is_coupon", false)}
                />
                Normal
              </label>
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  className="me-1"
                  checked={form.is_coupon}
                  disabled={isEdit}
                  onChange={() => update("is_coupon", true)}
                />
                Voucher/Coupon
              </label>
            </div>
            {isEdit && (
              <small className="text-muted d-block">The type can't be changed once a promotion is created.</small>
            )}
            {form.is_coupon && (
              <small className="text-muted">
                Redeemed only when this code is entered at checkout — won't auto-apply based on cart value.
              </small>
            )}
          </div>

          <div className="col-md-6">
            <label className="form-label">Promo Code</label>
            <input
              type="text"
              className="form-control"
              placeholder="e.g. WELCOME10"
              value={form.promo_code}
              onChange={(e) => update("promo_code", e.target.value.toUpperCase())}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Discount Type</label>
            <div className="d-flex gap-3" style={{ height: 38, alignItems: "center" }}>
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  className="me-1"
                  checked={form.offer_type === "per"}
                  onChange={() => update("offer_type", "per")}
                />
                Percent (%)
              </label>
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  className="me-1"
                  checked={form.offer_type === "rs"}
                  onChange={() => update("offer_type", "rs")}
                />
                Flat ($)
              </label>
            </div>
          </div>

          <div className="col-md-4">
            <label className="form-label">
              Discount Amount {form.offer_type === "per" ? "(%)" : "($)"}
            </label>
            <input
              type="number"
              min="0"
              className="form-control"
              value={form.offer_amount}
              onChange={(e) => update("offer_amount", e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Max Discount Cap ($)</label>
            <input
              type="number"
              min="0"
              className="form-control"
              value={form.max_discount_amount}
              onChange={(e) => update("max_discount_amount", e.target.value)}
            />
            {form.offer_type === "rs" && (
              <small className="text-muted">Flat discounts still need a cap — use the same value as the discount amount.</small>
            )}
          </div>

          <div className="col-md-4">
            <label className="form-label">Minimum Order Amount ($)</label>
            <input
              type="number"
              min="0"
              className="form-control"
              value={form.min_amount}
              onChange={(e) => update("min_amount", e.target.value)}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Valid From</label>
            <div className="d-flex gap-2">
              <input
                type="date"
                className="form-control"
                value={form.valid_from_date}
                onChange={(e) => update("valid_from_date", e.target.value)}
              />
              <input
                type="time"
                className="form-control"
                value={form.valid_from_time}
                onChange={(e) => update("valid_from_time", e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Valid To</label>
            <div className="d-flex gap-2">
              <input
                type="date"
                className="form-control"
                value={form.valid_to_date}
                onChange={(e) => update("valid_to_date", e.target.value)}
              />
              <input
                type="time"
                className="form-control"
                value={form.valid_to_time}
                onChange={(e) => update("valid_to_time", e.target.value)}
              />
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Applies To</label>
            <div className="d-flex flex-wrap gap-3">
              {ORDER_TYPE_OPTIONS.map((opt) => (
                <label key={opt.value} className="mb-0" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    className="me-1"
                    checked={form.offer_types.includes(opt.value)}
                    onChange={() => toggleInArray("offer_types", opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Payment Modes</label>
            <div className="d-flex flex-wrap gap-3">
              {PAYMENT_MODE_OPTIONS.map((opt) => (
                <label key={opt.value} className="mb-0" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    className="me-1"
                    checked={form.offer_valid_for.includes(opt.value)}
                    onChange={() => toggleInArray("offer_valid_for", opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div className="col-md-6">
            <label className="mb-0 d-block" style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                className="me-2"
                checked={form.one_time_use_only}
                onChange={(e) => update("one_time_use_only", e.target.checked)}
              />
              One-time use only (per customer)
            </label>
            <label className="mb-0 d-block mt-2" style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                className="me-2"
                checked={form.offer_available_web}
                onChange={(e) => update("offer_available_web", e.target.checked)}
              />
              Also show this promotion on the website
            </label>
          </div>

          {scheduleActive && (
            <PromotionScheduleControls
              form={form}
              onChange={(patch) =>
                setForm((prev) => ({ ...prev, ...(typeof patch === "function" ? patch(prev) : patch) }))
              }
              targetOptions={targetOptions}
            />
          )}

          {!scheduleActive && (
          <div className="col-12">
            <label className="form-label">Offer Product</label>
            <div className="d-flex gap-3 mb-2">
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  className="me-1"
                  checked={form.offer_dish_mode === "all"}
                  onChange={() => update("offer_dish_mode", "all")}
                />
                All Dishes
              </label>
              <label className="mb-0" style={{ cursor: "pointer" }}>
                <input
                  type="radio"
                  className="me-1"
                  checked={form.offer_dish_mode === "specific"}
                  onChange={() => update("offer_dish_mode", "specific")}
                />
                Specific Dishes
              </label>
            </div>

            {form.offer_dish_mode === "specific" && (
              <div className="border rounded p-2" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
                <input
                  type="text"
                  className="form-control form-control-sm mb-2"
                  placeholder="Search dishes..."
                  value={dishSearch}
                  onChange={(e) => setDishSearch(e.target.value)}
                />
                <div style={{ maxHeight: 180, overflowY: "auto" }}>
                  {!dishesLoaded ? (
                    <small className="text-muted">Loading dishes...</small>
                  ) : filteredDishes.length === 0 ? (
                    <small className="text-muted">No dishes match your search.</small>
                  ) : (
                    filteredDishes.map((dish) => (
                      <label key={dish.id} className="mb-1 d-block" style={{ cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          className="me-2"
                          checked={form.offer_dish_ids.includes(dish.id)}
                          onChange={() => toggleInArray("offer_dish_ids", dish.id)}
                        />
                        {dish.name}
                      </label>
                    ))
                  )}
                </div>
                {form.offer_dish_ids.length > 0 && (
                  <small className="text-muted">{form.offer_dish_ids.length} dish(es) selected</small>
                )}
              </div>
            )}
          </div>
          )}

          <div className="col-md-6">
            <label className="form-label">Promo Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              className="form-control"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            />
            {isEdit && <small className="text-muted">Leave empty to keep the current image.</small>}
          </div>

          <div className="col-12">
            <label className="form-label">Description</label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="e.g. Take 10% off your next order"
              value={form.details}
              onChange={(e) => update("details", e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={resetAndClose}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? (isEdit ? "Saving..." : "Creating...") : isEdit ? "Save Changes" : "Create Promotion"}
        </button>
      </div>
    </CommonModal>
  );
};

export default AddPromotionModal;
